import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingDomainAvailabilityQueryKey,
  getHostingSitesV2QueryKey,
  getSitesInfoQueryKey,
  getSitesListV2QueryKey,
  getUserHostingQueryKey,
  useDomains,
  useHostingDomainAvailability,
  useHostingSiteAddV2,
  useSitesInfo,
  useSitesSso,
} from '@newfold/huapi-js';
import {
  HostingSitesV2200ItemsItem,
  SiteInfoResponseModelV2,
} from '@newfold/huapi-js/src/index.schemas';

import useAlerts from '~/components/Alerts/alertsStore';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import ReassignDomainConfirmation from '~/components/ReassignDomainConfirmation';
import { HOSTING_MAX_FETCH_LIMIT } from '~/components/TenantProvider/utils/constants';
import useAccount from '~/hooks/useAccount';
import useAMRevamp from '~/hooks/useAMRevamp';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import ChooseTitle from '~/scenes/AddSetupSiteFlows/components/ChooseTitle';
import ConnectDomainWithCTB from '~/scenes/AddSetupSiteFlows/components/ConnectDomainWithCTB';
import ErrorModal from '~/scenes/AddSetupSiteFlows/components/ErrorModal';
import LaunchMigrationModal from '~/scenes/AddSetupSiteFlows/components/LaunchMigrationModal';
import MultiStepCard from '~/scenes/AddSetupSiteFlows/components/MultiStepCard';
import TransferSiteV2 from '~/scenes/AddSetupSiteFlows/components/TransferSiteV2';
import { useAddSiteContext } from '~/scenes/AddSetupSiteFlows/context/AddSiteContext';
import { handleDomainFlow } from '~/scenes/Site/scenes/Domains/utils/helpers';
import { canonicalizeUrl } from '~/utils/site/helpers';

import AddSiteOptionsWithCTB from '../components/AddSiteOptionsWithCTB';

type AddSiteWithCTBPropOptions = {
  siteList?: (HostingSitesV2200ItemsItem | SiteInfoResponseModelV2)[];
  redirectSSO?: VoidFunction;
  setChosenSiteId?: (state: number) => void;
  chosenSiteId?: string | number;
  changeLoading?: boolean;
  setChangeLoading?: (state: boolean) => void;
};

const suffixMap: Record<string, string> = {
  wordpress: 'title',
  MigrateSite: 'transfer',
  generic: 'title',
};

const AddSiteWithCTB = ({
  siteList = [],
  redirectSSO = () => {},
  setChosenSiteId = () => {},
  chosenSiteId = undefined,
  changeLoading = false,
  setChangeLoading = () => {},
}: AddSiteWithCTBPropOptions) => {
  const { t } = useTranslation(['addSites', 'domains', 'errors']);
  const { isAMRevamp } = useAMRevamp();
  const [, { generateAlert }] = useAlerts();
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const queryClient = useQueryClient();
  const { handleErrors } = useErrorHandler();
  const { hostingId: hostingIdParam } = useParams();
  const { id: hostingId } = useAccount();

  const { brandFromJWT } = useBrandInfo();
  const brand = brandFromJWT.toLowerCase();

  const [existingSiteInfo, setExistingSiteInfo] = useState<
    HostingSitesV2200ItemsItem | SiteInfoResponseModelV2
  >({});
  const [openLaunchModal, setOpenLaunchModal] = useState(false);
  const location = useLocation();
  const isTransferPage = location?.pathname?.endsWith('transfer');

  const { formData, setFormData, optionSelected, clearFormData } =
    useAddSiteContext();

  const queryKeyHostingSites = getHostingSitesV2QueryKey(hostingId);
  const queryKeySites = getSitesListV2QueryKey();
  const queryKeys = useMemo(
    () => [queryKeyHostingSites, queryKeySites],
    [queryKeyHostingSites, queryKeySites],
  );

  const [header, setHeader] = useState('');

  // Preserve whichever base segment we entered under: `sites` or `package/sites`.
  // This prevents the effect below from forcing the URL back to `/sites/add/...`
  // when the user landed via `/{hostingId}/package/websites/add/...`.
  const baseSegment = location?.pathname?.includes('/package/websites/')
    ? 'package/websites'
    : 'sites';

  const expectedPaths = useMemo(() => {
    if (isAMRevamp) {
      const suffix = optionSelected ? suffixMap[optionSelected] : undefined;

      if (!suffix) {
        return [`/${baseSegment}/add`, `/${hostingId}/${baseSegment}/add`];
      }

      return [
        `/${baseSegment}/add/${suffix}`,
        `/${hostingId}/${baseSegment}/add/${suffix}`,
      ];
    }

    return [`/${baseSegment}/add`];
  }, [baseSegment, hostingId, isAMRevamp, optionSelected]);

  useEffect(() => {
    const hasRequiredFields = formData?.subtype && formData?.siteName;
    const isOnExpectedPath = expectedPaths.includes(location?.pathname);

    if (hasRequiredFields || isOnExpectedPath) {
      return;
    }

    if (isAMRevamp) {
      if (!optionSelected) {
        return;
      }

      const suffix = suffixMap[optionSelected];

      if (!suffix) {
        return;
      }

      const defaultPath = `/${baseSegment}/add/${suffix}`;
      const hostingPath = `/${hostingId}/${baseSegment}/add/${suffix}`;
      navigate(hostingIdParam ? hostingPath : defaultPath);
      return;
    }

    navigate('');

    // Need to comment out lint rule to ensure function only runs ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryCountSiteInfo = useRef(0);

  const handleOnClose = () => {
    setExistingSiteInfo({});
  };

  const hookOptions = {
    query: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  };

  const { refetch } = useSitesSso(
    Number(chosenSiteId),
    {
      bounce_name: 'wpmigration',
    },
    hookOptions,
  );
  // TODO: replace with hostingDomainsV2
  // Get a list of all domains on this hosting account
  const { data: domainListFull } = useDomains(hostingId, undefined, {
    query: {
      enabled: !!hostingId,
      select: (data) => [...data?.data?.domains],
    },
  });
  const {
    data: domainAvailability,
    refetch: fetchDomainAvailability,
    isLoading: isLoadingDomainAvailability,
    isError: isErrorDomainAvailability,
    error: domainAvailabilityError,
  } = useHostingDomainAvailability(
    hostingId,
    {
      domain: formData.domain,
    },
    {
      query: {
        enabled: false,
        staleTime: 0, // disable cache
      },
    },
  );

  const retryMigration = useCallback(
    (retryFunc = () => {}) => {
      return generateAlert({
        title: t('migrateSiteOptions.onError.title'),
        description: (
          <Trans
            i18nKey="addSites:migrateSiteOptions.onError.description"
            values={{ phoneNumber: '1-888-401-4678' }}
            components={{ Link: <Link href={'tel:+18884014678'}></Link> }}
          />
        ),
        severity: 'error',
        showCloseBtn: true,
        ctaText: t('migrateSiteOptions.onError.ctaText'),
        cta: retryFunc,
        autoHideDuration: 10000,
      });
    },
    [generateAlert, t],
  );

  const launchMigration = useCallback(() => {
    setChangeLoading(false);
    setOpenLaunchModal(true);
  }, [setChangeLoading]);

  const redirectToWPMigrationUrl: any = async () => {
    const wordpressSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/wordpress-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/wordpress.html`;

    // Actually open the window
    var ssoWindow = window.open(wordpressSsoLoadingPage, '_blank');
    setChangeLoading(true);

    const { data, error } = await refetch();

    if (error) {
      // close the window
      ssoWindow?.close();
      setChangeLoading(false);
      return retryMigration(() => redirectToWPMigrationUrl());
    }
    const url = data?.data?.sso;

    if (url && ssoWindow) {
      // set url for window
      ssoWindow.location = url;

      setChangeLoading(false);
      setOpenLaunchModal(false);
      hostingNavigate();
      return;
    }

    ssoWindow?.close();

    generateAlert({
      severity: 'error',
      description: t('ssoLink.notFound'),
      showCloseBtn: true,
    });
    setChangeLoading(false);
    setOpenLaunchModal(false);
  };

  const customSiteInfoQueryKey = [
    ...getSitesInfoQueryKey(Number(chosenSiteId)),
    '/siteInfoInAdd',
  ];
  const {
    data: siteInfo,
    refetch: refetchSiteInfo,
    isSuccess: isSuccessSiteInfo,
  } = useSitesInfo(Number(chosenSiteId), {
    query: {
      queryKey: customSiteInfoQueryKey,
      gcTime: 0,
      enabled: false,
    },
  });

  useEffect(() => {
    if (isSuccessSiteInfo && changeLoading) {
      (async () => {
        if (siteInfo?.data?.status === 'active') {
          const keysToInvalidate = isAMRevamp
            ? [
                ...queryKeys,
                getUserHostingQueryKey({
                  page: 1,
                  limit: HOSTING_MAX_FETCH_LIMIT,
                }),
              ]
            : queryKeys;
          await invalidateMultipleQueries(queryClient, keysToInvalidate);

          if (siteInfo?.data?.type === 'wordpress') {
            !isTransferPage ? redirectSSO() : launchMigration();
          } else {
            navigate(chosenSiteId ? `/sites/${chosenSiteId}` : '/');
          }
          return siteInfo;
        }
        // If the status is not active yet and it's a transfer page, keep refetching until it becomes active
        if (isTransferPage) {
          if (siteInfo?.data?.status === 'failed_create') {
            // TODO: resync the site call
            setChangeLoading(false);
            return retryMigration(() => refetchSiteInfo());
          }
          setTimeout(() => refetchSiteInfo(), 5000);
        } else {
          // If site is NOT active yet, retry up to 5 times in 5-10 second intervals
          // If site is still NOT active, redirect to site list page
          retryCountSiteInfo.current += 1;
          if (retryCountSiteInfo.current <= 5) {
            const delay = retryCountSiteInfo.current <= 3 ? 5000 : 10000;
            setTimeout(() => refetchSiteInfo(), delay);
          } else {
            generateAlert({
              title: t('alertCreatingSite.title'),
              description: t('alertCreatingSite.desc'),
              severity: 'info',
              showCloseBtn: true,
            });
            setChangeLoading(false);
            hostingNavigate();
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSiteInfo, siteInfo, chosenSiteId]);

  const setAlertMessage = (
    variant: string,
    desc: JSX.Element | string,
    persist: boolean = false,
  ) => {
    generateAlert({
      severity: variant,
      description: desc,
      showCloseBtn: true,
      persist,
    });
  };

  const { mutate: createWebsite, isPending: isSiteAddPending } =
    useHostingSiteAddV2({
      mutation: {
        onMutate: () => {},
        onSuccess: async (data) => {
          setChangeLoading(true);
          await invalidateMultipleQueries(queryClient, queryKeys);
          if (formData.migrationId) {
            setTimeout(() => {
              hostingNavigate();
            }, 10000);
          } else if (formData?.isMigrating) {
            //TODO: fix the huapi schema or update the huapi resp to return id instead of site_id
            // @ts-expect-error
            setChosenSiteId(data?.data?.site_id);
          } else {
            // @ts-expect-error
            setChosenSiteId(data?.data?.site_id);
            // will trigger site info call to check site status after 10 seconds
            setTimeout(() => refetchSiteInfo(), 10000);
          }
          clearFormData();
        },
        onError: (error: any) => {
          if (formData.migrationId) {
            setAlertMessage(
              'error',
              t('addSites:migrateSiteOptions.transferError'),
            );
          } else if (error?.response?.data?.error === 'urlInUse') {
            // TODO: Get site info from BE error response to avoid looping through site list
            const existingSite = siteList.find((site) =>
              canonicalizeUrl(site?.url!).endsWith(
                canonicalizeUrl(`://${formData.domain}/${formData.path}`),
              ),
            );
            if (existingSite) {
              setExistingSiteInfo(existingSite);
            } else {
              generateAlert({
                title: t('alert.urlInUseTitle'),
                description: t('alert.urlInUseDesc', {
                  path: canonicalizeUrl(`${formData.domain}/${formData.path}`),
                }),
                severity: 'error',
                showCloseBtn: true,
                persist: true,
              });
            }
          } else if (isTransferPage || formData?.isMigrating) {
            return retryMigration(() =>
              createWebsite({
                hostingId,
                data: {
                  subtype: 'wordpress',
                },
              }),
            );
          } else {
            handleErrors(error);
          }
          setChangeLoading(false);
        },
      },
    });

  const domainAvailableKey = getHostingDomainAvailabilityQueryKey(hostingId, {
    domain: formData.domain,
  });

  const handleCreateWebsite = (skip_pointer: boolean) => {
    createWebsite({
      hostingId,
      data: {
        title: formData.siteName,
        domain: formData.domain,
        path: formData.path,
        subtype: formData.subtype,
        skip_pointer,
      },
    });
  };

  const setErrorExistingDomainAlert = (error: any) => {
    const { error: errorType } = error.response.data;

    const errorMessages: { [key: string]: string } = {
      invalidDomain: t('domainInvalid'),
      domainAvailabilityInValidTldError: t('domainInvalid'),
    };

    const errorMessage = errorMessages[errorType] || t('alertError');

    generateAlert({
      severity: 'error',
      description: errorMessage,
      showCloseBtn: true,
      persist: false,
    });
  };

  useEffect(() => {
    if (isErrorDomainAvailability && domainAvailabilityError) {
      setErrorExistingDomainAlert(domainAvailabilityError);
      // Need to remove this else this method setErrorExistingDomainAlert gets called infinitely
      queryClient.removeQueries({ queryKey: domainAvailableKey });
    }
    if (domainAvailability && domainAvailability.data) {
      const { isError, code, skipSFAPI } = handleDomainFlow(
        domainAvailability?.data,
      );
      if (isError) {
        code === 'existingDomainAddError'
          ? generateAlert({
              description: (
                <Trans
                  i18nKey="domains:domainAssignValidations:existingDomainAddError"
                  components={{ strong: <strong /> }}
                ></Trans>
              ),
              severity: 'error',
              showCloseBtn: true,
            })
          : setAlertMessage(
              'error',
              t(`domains:domainAssignValidations.${code}`),
            );
      } else {
        handleCreateWebsite(skipSFAPI);
      }
    }
    // Need to remove this else this method handleDomainFlow gets called infinitely
    queryClient.removeQueries({ queryKey: domainAvailableKey });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainAvailabilityError, isErrorDomainAvailability, domainAvailability]);

  const handleSubmit = () => {
    const isDomainPresentInAnotherSite =
      !formData?.path &&
      domainListFull?.some(({ domain }) => domain === formData?.domain);

    if (isDomainPresentInAnotherSite) {
      // TODO: update pointer to current site
      generateAlert({
        severity: 'error',
        description: <Trans i18nKey="errors:domainInUse" />,
        showCloseBtn: true,
        persist: true,
      });
    } else {
      // Fetches the domain availability status
      fetchDomainAvailability();
    }
  };

  const headingText =
    header === 'transfer' ? 'migrateSiteOptions.title' : 'addSite';

  const handleDomainReassign = () => {
    createWebsite({
      hostingId,
      data: {
        title: formData.siteName,
        domain: formData.domain,
        path: formData.path,
        subtype: formData.subtype,
        force_vhost: true,
      },
    });
  };

  const handleCloseInstallModal = () => hostingNavigate();
  const handleCancelReassignDomain = () => navigate('connect');

  useEffect(() => {
    if (isTransferPage) {
      setHeader('transfer');
    } else {
      setHeader('addsite');
    }
  }, [isTransferPage]);

  return (
    <>
      <Container data-testid="shared-add-site-ctb-flow">
        <Stack spacing={3}>
          <Typography variant="h1" color="primary">
            {t(headingText)}
          </Typography>
          <MultiStepCard>
            <Routes>
              <Route
                index
                element={
                  <AddSiteOptionsWithCTB
                    formData={formData}
                    setFormData={setFormData}
                    createWebsite={createWebsite}
                    isPending={changeLoading || isSiteAddPending}
                  />
                }
              />
              <Route
                path="title/*"
                element={
                  <ChooseTitle
                    formData={formData}
                    setFormData={setFormData}
                    changeLoading={changeLoading}
                  />
                }
              />
              <Route
                path="connect/*"
                element={
                  <ConnectDomainWithCTB
                    formData={formData}
                    setFormData={setFormData}
                    createWebsite={createWebsite}
                    changeLoading={changeLoading}
                    isSiteAddPending={isSiteAddPending}
                    isLoadingDomainAvailability={isLoadingDomainAvailability}
                    handleSubmit={handleSubmit}
                  />
                }
              />
              <Route
                path="reassign/*"
                element={
                  <ReassignDomainConfirmation
                    domain={formData?.domain}
                    siteName={formData?.siteName}
                    siteSubtype={formData?.subtype}
                    loading={changeLoading}
                    isPendingAdd={isSiteAddPending}
                    showInstallationModal
                    handleCloseInstallModal={handleCloseInstallModal}
                    handleCancelBtn={handleCancelReassignDomain}
                    handleProceedBtn={handleDomainReassign}
                  />
                }
              />
              <Route
                path="transfer/*"
                element={
                  <TransferSiteV2
                    changeLoading={changeLoading}
                    refetchSiteInfo={refetchSiteInfo}
                  />
                }
              />
            </Routes>
          </MultiStepCard>
        </Stack>
      </Container>
      <ErrorModal
        open={!!existingSiteInfo?.id}
        errorTitle={t('urlTaken')}
        paragraphOne={
          <Trans
            i18nKey="addSites:alreadySiteName"
            values={{
              path: existingSiteInfo.url,
            }}
          />
        }
        paragraphTwo={
          <Trans
            i18nKey="addSites:delete"
            values={{ name: existingSiteInfo.name }}
            components={{
              Link: (
                <Link href={`/sites/${existingSiteInfo.id}/settings`}></Link>
              ),
            }}
          />
        }
        handleOnClose={handleOnClose}
      />
      <LaunchMigrationModal
        open={openLaunchModal}
        handleContinue={redirectToWPMigrationUrl}
        isPending={changeLoading}
      />
    </>
  );
};

export default AddSiteWithCTB;
